import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import apiService from '../../services/api';
import HourlySuccessfulTrafficTable from '../HourlySuccessfulTrafficTable';
import HourlyUnsuccessfulTrafficTable from '../HourlyUnsuccessfulTrafficTable';
import PlatformStatus from './PlatformStatus';
import TechnicalStatus from './TechnicalStatus';
import EnterpriseStatus from './EnterpriseStatus';
import RecentActivity from '../RecentActivity';
import CommercialStatus from './CommercialStatus';
import FinanceStatus from './FinanceStatus';
import { hasModulePermission, hasPermission, hasFeatureSubscription } from '../../utils/permissionUtils.js';

const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const userPermissions = JSON.parse(sessionStorage.getItem('userPermissions') || '[]');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load stats and recent activity in parallel
      const [statsResponse, activityResponse] = await Promise.all([
        apiService.getDashboardStats(),
        apiService.getRecentActivity()
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }

      if (activityResponse.success) {
        setRecentActivity(activityResponse.data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    loadDashboardData();
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-speedometer2 me-2"></i>
              Dashboard
            </h2>
            <Button variant="outline-primary" onClick={refreshData}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Commercial and Finance Status */}
      <Row className="mb-4">
        {hasModulePermission(userPermissions, '_MODULE_COMMERCIAL_') && (
          <Col lg={6} md={6} className="mb-4">
            <CommercialStatus stats={stats} />
          </Col>
        )}
        {hasModulePermission(userPermissions, '_MODULE_FINANCE_') && (
          <Col lg={6} md={6} className="mb-4">
            <FinanceStatus stats={stats} />
          </Col>
        )}
      </Row>


      {/* Hourly Traffic Tables */}
      {hasPermission(userPermissions, '_SECTION_UTILIZATION_STATS_') && (
        <Row className="mt-4">
          <Col>
            <HourlySuccessfulTrafficTable />
          </Col>
        </Row>
      )}

      {/* Three Column Layout: Recent Activity, Platform & Enterprise Status, Technical Status */}
      <Row className="mt-4">
        {hasPermission(userPermissions, '_SECTION_PLATFORM_USERS_') && (
          <Col lg={4}>
            <RecentActivity recentActivity={recentActivity} />
          </Col>
        )}

        <Col lg={4}>
          {hasPermission(userPermissions, '_SECTION_PLATFORM_USERS_') && (
            <Row className="mb-4">
              <Col lg={12}>
                <PlatformStatus stats={stats} />
              </Col>
            </Row>
          )}
          {hasPermission(userPermissions, '_SECTION_ENTERPRISE_USERS_') && (
            <Row>
              <Col lg={12}>
                <EnterpriseStatus stats={stats} />
              </Col>
            </Row>
          )}
        </Col>
        {hasPermission(userPermissions, '_SECTION_INFRASTRUCTURE_STATUS_') && (

          <Col lg={4}>
            <TechnicalStatus stats={stats} />
          </Col>
        )}
      </Row>


      {hasPermission(userPermissions, '_SECTION_AUDIT_') && (
        <Row className="mt-4">
          <Col>
            <HourlyUnsuccessfulTrafficTable />
          </Col>
        </Row>
      )}


    </div>
  );
};

export default Dashboard;
